import Swiper from 'swiper';

export interface MarqueeMethods {
    /**
     * Whether marquee enabled and running
     */
    running: boolean;

    /**
     * Start marquee
     */
    start(): boolean;

    /**
     * Stop marquee
     */
    stop(): boolean;
}

export interface MarqueeEvents {
    /**
     * Event will be fired in when marquee started
     */
    marqueeStart: (swiper: Swiper) => void;
    /**
     * Event will be fired when marquee stopped
     */
    marqueeStop: (swiper: Swiper) => void;
    /**
     * Event will be fired when slide changed with marquee
     */
    marquee: (swiper: Swiper) => void;
}

export interface MarqueeOptions {
    enabled: boolean;
    /**
     * When enabled marquee will not be stopped when it reaches last slide. It will run in reverse direction.
     *
     * @default false
     */
    bounce?: boolean;
    /**
     * When enabled marquee will wait for wrapper transition to continue.
     * Can be disabled in case of using Virtual Translate when your
     * slider may not have transition
     *
     * @default true
     */
    waitForTransition?: boolean;
    /**
     * Set to `false` and marquee will not be disabled after user interactions (swipes),
     * it will be restarted every time after interaction
     *
     * @default false
     */
    disableOnInteraction?: boolean;
    /**
     * Enable this parameter and marquee will be stopped when it reaches last slide (has no effect in loop mode)
     *
     * @default false
     */
    stopOnLastSlide?: boolean;
    /**
     * Enables marquee in reverse direction
     *
     * @default false
     */
    reverseDirection?: boolean;
    /**
     * When enabled marquee will be paused on mouse enter over Swiper container.
     * If `disableOnInteraction` is also enabled, it will stop marquee instead of pause
     *
     * @default true
     */
    pauseOnMouseEnter?: boolean;
    watchSlidesProgress?: boolean;
    /**
     * Marquee effect speed in `px`.
     * Value `1` is equal and 1px per request animation frame (60 times per second).
     * Between `0` and `1` it is slower, more then `1` it is faster
     *
     * @default 1
     */
    speed?: number;
}

export enum MarqueeDirection {
    Prev = 'prev',
    Next = 'next'
}

export function Marquee({ swiper, extendParams, on, emit }) {
    let _requestAnimationFrame: number | undefined;

    swiper.marquee = {
        initialized: false,
        running: false,
        paused: false,
        direction: MarqueeDirection.Next
    };

    extendParams({
        marquee: {
            enabled: false,
            bounce: false,
            waitForTransition: true,
            disableOnInteraction: false,
            stopOnLastSlide: false,
            reverseDirection: false,
            pauseOnMouseEnter: true,
            watchSlidesProgress: true,
            speed: 1
        }
    });

    function run() {
        if (!swiper.enabled || !swiper.marquee.running || swiper.marquee.paused || swiper.width >= swiper.virtualSize) {
            return;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
        }
        _requestAnimationFrame = requestAnimationFrame(() => {
            let marqueeResult = false;

            if (swiper.marquee.direction === MarqueeDirection.Prev) {
                if (swiper.params.loop) {
                    swiper.loopFix();

                    emit('marquee');
                } else if (!swiper.isBeginning) {
                    emit('marquee');
                } else if (!swiper.params.marquee.stopOnLastSlide) {
                    if (swiper.params.marquee.bounce) {
                        swiper.marquee.direction = MarqueeDirection.Next;
                    } else {
                        marqueeResult = swiper.slideTo(swiper.slides.length - 1, swiper.params.speed, true, false);
                    }
                    emit('marquee');
                } else {
                    stop();
                }
            } else if (swiper.params.loop) {
                swiper.loopFix();

                emit('marquee');
            } else if (!swiper.isEnd) {
                emit('marquee');
            } else if (!swiper.params.marquee.stopOnLastSlide) {
                if (swiper.params.marquee.bounce) {
                    swiper.marquee.direction = MarqueeDirection.Prev;
                } else {
                    marqueeResult = swiper.slideTo(0, swiper.params.speed, true, true);
                }

                emit('marquee');
            } else {
                stop();
            }

            const update = () => {
                const diff =
                    swiper.marquee.direction === MarqueeDirection.Prev ? swiper.params.marquee.speed : -1 * swiper.params.marquee.speed;

                swiper.swipeDirection = swiper.marquee.direction;
                const translate = diff + swiper.getTranslate();

                // Update progress
                swiper.updateProgress(translate);
                // Update translate
                swiper.setTranslate(translate);

                swiper.transitionStart(true, swiper.swipeDirection);

                // Update active index in free mode
                if (
                    (swiper.params.freeMode && swiper.params.freeMode.enabled && swiper.freeMode) ||
                    swiper.params.watchSlidesProgress ||
                    swiper.params.marquee.watchSlidesProgress
                ) {
                    swiper.updateActiveIndex();
                    swiper.updateSlidesClasses();
                }

                run();
            };

            if (swiper.params.cssMode && swiper.marquee.running) {
                update();
            } else if (marqueeResult === false) {
                update();
            }
        });
    }

    function start() {
        if (!swiper.enabled) {
            return false;
        }

        if (typeof _requestAnimationFrame !== 'undefined') {
            return false;
        }

        if (swiper.marquee.running) {
            return false;
        }

        swiper.marquee.running = true;

        emit('marqueeStart');
        run();

        return true;
    }

    function stop() {
        if (!swiper.marquee.running) {
            return false;
        }

        if (typeof _requestAnimationFrame === 'undefined') {
            return false;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
            _requestAnimationFrame = undefined;
        }

        swiper.marquee.running = false;

        emit('marqueeStop');

        return true;
    }

    function pause(speed?: number) {
        if (!swiper.marquee.running) {
            return;
        }

        if (swiper.marquee.paused) {
            return;
        }

        if (_requestAnimationFrame) {
            cancelAnimationFrame(_requestAnimationFrame);
            _requestAnimationFrame = undefined;
        }

        swiper.marquee.paused = true;

        if (speed === 0 || !swiper.params.marquee.waitForTransition) {
            swiper.marquee.paused = false;
            run();
        } else {
            ['transitionend', 'webkitTransitionEnd'].forEach(event => {
                swiper.$wrapperEl[0].addEventListener(event, onTransitionEnd);
            });
        }
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'hidden' && swiper.marquee.running) {
            pause();
        }

        if (document.visibilityState === 'visible' && swiper.marquee.paused) {
            swiper.marquee.paused = false;
            run();
        }
    }

    function onTransitionEnd(e: TransitionEvent) {
        if (!swiper || swiper.destroyed || !swiper.$wrapperEl) {
            return;
        }

        if (e.target !== swiper.$wrapperEl[0]) {
            return;
        }

        ['transitionend', 'webkitTransitionEnd'].forEach(event => {
            swiper.$wrapperEl[0].removeEventListener(event, onTransitionEnd);
        });

        swiper.marquee.paused = false;

        if (!swiper.marquee.running) {
            stop();
        } else {
            run();
        }
    }

    function onMouseEnter() {
        if (swiper.params.marquee.disableOnInteraction) {
            stop();
        } else {
            emit('marqueePause');
            pause();
        }

        ['transitionend', 'webkitTransitionEnd'].forEach(event => {
            swiper.$wrapperEl[0].removeEventListener(event, onTransitionEnd);
        });
    }

    function onMouseLeave() {
        if (swiper.params.marquee.disableOnInteraction) {
            return;
        }

        swiper.marquee.paused = false;

        emit('marqueeResume');
        run();
    }

    function attachMouseEvents() {
        if (swiper.params.marquee.pauseOnMouseEnter) {
            swiper.$el.on('mouseenter', onMouseEnter);
            swiper.$el.on('mouseleave', onMouseLeave);
        }
    }

    function detachMouseEvents() {
        swiper.$el.off('mouseenter', onMouseEnter);
        swiper.$el.off('mouseleave', onMouseLeave);
    }

    function init() {
        swiper.marquee.direction = swiper.params.marquee.reverseDirection ? MarqueeDirection.Prev : MarqueeDirection.Next;

        start();

        document.addEventListener('visibilitychange', onVisibilityChange);

        attachMouseEvents();

        swiper.marquee.initialized = true;
    }

    on('init', () => {
        if (swiper.params.marquee.enabled) {
            init();
        }
    });

    on('update', () => {
        if (swiper.params.marquee && swiper.params.marquee.enabled && !swiper.marquee.initialized) {
            init();
        }
    });

    on('beforeTransitionStart', (_s: Swiper, speed?: number, internal?: boolean) => {
        if (swiper.marquee.running) {
            if (internal || !swiper.params.marquee.disableOnInteraction) {
                pause(speed);
            } else {
                stop();
            }
        }
    });

    on('sliderFirstMove', () => {
        if (swiper.marquee.running) {
            if (swiper.params.marquee.disableOnInteraction) {
                stop();
            } else {
                pause();
            }
        }
    });

    on('touchEnd', () => {
        if (swiper.params.cssMode && swiper.marquee.paused && !swiper.params.marquee.disableOnInteraction) {
            run();
        }
    });

    on('destroy', () => {
        detachMouseEvents();

        if (swiper.marquee.running) {
            stop();
        }

        document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    Object.assign(swiper.marquee, {
        pause,
        run,
        start,
        stop
    });
}
